import React, { useContext, useEffect } from 'react';

import { hot } from 'react-hot-loader/root';

import { TrackEventContext } from 'common/containers/EventContainer';
import OutlierLandingHelmet from 'common/helmets/OutlierLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';

import OutlierWordmark from 'img/landing/outlier-wordmark.svg';

import 'css/components/www/_LandingCustomersPage.scss';

const OutlierLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Outlier Case Study');
  }, [trackEvent]);

  return (
    <main className="landingCustomersPage">
      <OutlierLandingHelmet />
      <section className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How Outlier scored big with product-led growth and community engagement" />
          <div className="headerVideo">
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/zXA4A5hRgic"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen></iframe>
          </div>
        </div>
      </section>
      <section className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              Evan Kirkham is the co-founder of Outlier. He's always been a sports fan, so he
              created a smart sports betting app for people like&nbsp;him.
            </p>
            <p>
              Outlier attracted hundreds of thousands of users in only 13 months. With just eight
              team members, productivity and exceptional performance are a&nbsp;must.
            </p>
            <h2 className="sectionTitle">Problem</h2>
            <p>
              Outlier is a product-led company. Naturally, user feedback is very important for Evan
              and the entire team. So, they focused on gathering as much feedback as possible right
              away, and they&nbsp;succeeded!
            </p>
            <p className="quote">
              &ldquo;We started getting too much feedback and didn't have a good way to catalog and
              triage it. We now get hundreds of pieces of feedback a week. We tried everything from
              spreadsheets to long Slack&nbsp;threads.&rdquo;
            </p>
            <p>
              Volume wasn't the only issue. Lots of feature requests were getting “lost in
              translation.” Finding and combining similar requests was also
              very&nbsp;time-consuming.
            </p>
            <p>
              Finally, internal communication was affected too. The product team would plan the
              roadmap, but it was hard for marketing to know the status of each request. There was
              no&nbsp;visibility.
            </p>
            <h2 className="sectionTitle">Solution</h2>
            <p>
              Canny checked all the boxes for Outlier. It could help with feedback management,
              prioritization, roadmapping, and update&nbsp;announcements.
            </p>
            <p>
              Evan considers himself a non-technical person. Despite that, he found Canny very easy
              to set&nbsp;up.
            </p>
            <p className="quote">
              &ldquo;Sometimes, new SaaS tools can be a bit confusing. But with Canny, I could start
              using it right&nbsp;away.&rdquo;
            </p>
            <p>
              Having tools that help you align with your strategic goals is essential. Canny does
              that for&nbsp;Outlier.
            </p>
            <p>Evan sees product-led growth in five&nbsp;steps:</p>
            <ol>
              <li>Feedback collection</li>
              <li>Thanking users for feedback</li>
              <li>Triaging feedback</li>
              <li>Developing the feature</li>
              <li>Alerting the person about the&nbsp;update</li>
            </ol>
            <p className="quote">
              &ldquo;Canny helps with each step in this process, so it fits
              us&nbsp;perfectly.&rdquo;
            </p>
            <p>
              Linear and Intercom integrations speed up this process even further. When a user
              submits feedback via Intercom, adding it to Canny is super easy. Then, the team uses
              Linear and Canny to actually triage that&nbsp;feedback.
            </p>
            <p className="quote">
              &ldquo;Sometimes you get requests that aren't exactly duplicates, but they're pretty
              dang close. We can find and merge these two posts with Canny to keep the board clean.
              I find this very&nbsp;powerful.&rdquo;
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              Canny has become an integral part of Outlier. Their product team relies on numbers in
              Canny to assess their&nbsp;effectiveness.
            </p>
            <p className="quote">
              &ldquo;We discuss the number of posts, comments, votes, and status changes we receive
              weekly. We want to see each of these go up every week. That's an indicator of how
              well-greased our growth machine is. Status changes are especially great indicators of
              that. This tells us that we've handled the feedback in some&nbsp;way.&rdquo;
            </p>
            <p>
              Canny also helps Evan with community engagement. He primarily works in marketing, so
              staying in touch with customers is super&nbsp;important.
            </p>
            <p className="quote">
              &ldquo;We're pretty open about our roadmap. We often share Canny screenshots with our
              community on Discord and social media. Building in the open has built a ton of trust
              with our user&nbsp;base.&rdquo;
            </p>
            <p>Canny is helping Outlier save some costs&nbsp;too.</p>
            <p className="quote">
              &ldquo;If it weren't for Canny, we'd have to hire a product manager way sooner. The
              whole team and I save so much time by cataloging and triaging feedback so easily. I
              never want to go back to pre-Canny&nbsp;days.&rdquo;
            </p>
            <p>
              Before Canny, Outlier came across some communication issues. Keeping everyone
              up-to-date was becoming a challenge. Now, anyone on the team can quickly check Canny
              and see the status of each&nbsp;request.
            </p>
            <p className="quote">
              &ldquo;I think one of the greatest impacts of Canny is the improved
              cross-team&nbsp;collaboration.&rdquo;
            </p>
            <p>Evan leaves us with one piece of&nbsp;advice:</p>
            <p className="quote">
              &ldquo;Think through your business goals. Then, use Canny to coordinate between
              product and marketing teams. It's a great tool for that coordination (in addition to
              feedback&nbsp;management).&rdquo;
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage src={OutlierWordmark} alt="Outlier's Logo" />
            Outlier is a sports betting platform where sports fans can browse, analyze, and execute
            bets with&nbsp;confidence.
            <a href="https://outlier.bet/" rel="noopener" target="_blank">
              outlier.bet/
            </a>
            <div className="stat">
              <UppercaseHeader>Employees</UppercaseHeader>
              <p>8</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Founded</UppercaseHeader>
              <p>2023</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2023</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>3,000+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded />
      </section>
    </main>
  );
};

export default hot(OutlierLanding);
